'use client'
import { useHeaderTheme } from '@/providers/HeaderTheme'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'
import { Menu, X, Phone, Mail } from 'lucide-react'

import type { Header } from '@/payload-types'

import { Logo } from '@/components/Logo/Logo'
// import { HeaderNav } from './Nav'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import { cn } from '@/utilities/cn'

interface HeaderClientProps {
  data: Header
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ data }) => {
  const [theme, setTheme] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()

  useEffect(() => {
    setHeaderTheme(null)
    setMobileMenuOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTheme])

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
        )}
        {...(theme ? { 'data-theme': theme } : {})}
      >
        {/* Top Bar with Contact Info */}
        <div className="bg-primary text-white py-2 hidden md:block">
          <div className="container mx-auto px-4 flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              {data?.contact?.phone && (
                <a href={`tel:${data.contact.phone}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                  <Phone className="w-4 h-4" />
                  {data.contact.phone}
                </a>
              )}
              {data?.contact?.email && (
                <a href={`mailto:${data.contact.email}`} className="flex items-center gap-2 hover:text-white/80 transition-colors">
                  <Mail className="w-4 h-4" />
                  {data.contact.email}
                </a>
              )}
            </div>
            {data?.contact?.tagline && (
              <div className="text-white/80">
                {data.contact.tagline}
              </div>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <div className="bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center h-24">
              {/* Logo */}
              <div className="flex-shrink-0">
                <Link href="/" className="relative z-50 inline-block">
                  {data?.logo && typeof data.logo !== 'string' ? (
                    <Media 
                      resource={data.logo} 
                      imgClassName="h-16 w-auto object-contain"
                      priority
                    />
                  ) : (
                    <Logo loading="eager" priority="high" className="h-16 w-auto" />
                  )}
                </Link>
              </div>

              {/* Desktop Navigation - Right aligned */}
              <div className="hidden lg:flex flex-1 items-center justify-end gap-8">
                <nav className="flex items-center gap-8">
                  {data?.navItems?.map(({ link }, i) => (
                    <CMSLink 
                      key={i} 
                      {...link} 
                      appearance="link"
                      className="text-secondary font-medium hover:text-primary transition-colors duration-200"
                    />
                  ))}
                </nav>

                {/* CTA Button */}
                {data?.cta?.show && data?.cta?.link && (
                  <CMSLink 
                    {...data.cta.link}
                    appearance="default"
                    className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105"
                  />
                )}
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden flex-1 flex justify-end">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 text-secondary"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={cn(
          "lg:hidden bg-white border-t border-gray-200 overflow-hidden transition-all duration-300",
          mobileMenuOpen ? "max-h-96" : "max-h-0"
        )}>
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col gap-4">
              {data?.navItems?.map(({ link }, i) => (
                <CMSLink 
                  key={i} 
                  {...link} 
                  appearance="link"
                  className="text-secondary font-medium hover:text-primary transition-colors duration-200 py-2"
                />
              ))}
              {data?.cta?.show && data?.cta?.link && (
                <CMSLink 
                  {...data.cta.link}
                  appearance="default"
                  className="bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 text-center mt-2"
                />
              )}
            </nav>
            
            {/* Mobile Contact Info */}
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
              {data?.contact?.phone && (
                <a href={`tel:${data.contact.phone}`} className="flex items-center gap-2 text-secondary text-sm">
                  <Phone className="w-4 h-4" />
                  {data.contact.phone}
                </a>
              )}
              {data?.contact?.email && (
                <a href={`mailto:${data.contact.email}`} className="flex items-center gap-2 text-secondary text-sm">
                  <Mail className="w-4 h-4" />
                  {data.contact.email}
                </a>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-24 md:h-[104px]"></div>
    </>
  )
}
